import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
// ArrowBackIcon removed — sidebar handles navigation
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  IconButton,
  CircularProgress,
  Tooltip,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  // ArrowBack as ArrowBackIcon,
  // CalendarMonth as CalendarIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Today as TodayIcon,
  AccessTime as AccessTimeIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  WhatsApp as WhatsAppIcon,
  ViewWeek as WeekViewIcon,
  CalendarViewMonth as MonthViewIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { auth } from '../../firebase';
import { NaraSimpleTypewriter, NaraTerminalCard, monoStyle } from './NaraTerminal';
import { publishNaraContext, clearNaraContext } from '../naraChatContext';

const API_URL = 'https://turismo.nara.chat/api';

const DIAS_SEMANA = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

const estadoColors = {
  confirmado: { bg: '#dcfce7', color: '#16a34a', label: 'Confirmada' },
  cancelado: { bg: '#fee2e2', color: '#dc2626', label: 'Cancelada' },
  pendiente: { bg: '#fef3c7', color: '#d97706', label: 'Pendiente' },
  null: { bg: '#fef3c7', color: '#d97706', label: 'Pendiente' }
};

const AgendaCitasPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [userId, setUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [citas, setCitas] = useState([]);
  const [estadisticas, setEstadisticas] = useState(null);
  const [fechaActual, setFechaActual] = useState(new Date());
  const [vista, setVista] = useState('semana');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [citaSeleccionada, setCitaSeleccionada] = useState(null);

  const seleccionarCita = (cita) => {
    setCitaSeleccionada(cita);
    if (cita) {
      const fecha = cita.fecha_cita ? new Date(cita.fecha_cita) : null;
      publishNaraContext('cita', {
        id: cita.id,
        paciente: cita.paciente_nombre,
        fecha: fecha ? fecha.toLocaleDateString('es') : null,
        hora: fecha ? fecha.toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' }) : null,
        estado: cita.estado,
        motivo: cita.motivo,
      });
    } else {
      clearNaraContext();
    }
  };

  // NARA Morning Briefing
  const [morningBriefing, setMorningBriefing] = useState(null);
  const [loadingBriefing, setLoadingBriefing] = useState(false);
  const [showBriefingAlerts, setShowBriefingAlerts] = useState(false);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (u) => {
      if (u) {
        setUser(u);
        const storedId = localStorage.getItem('user_id');
        if (storedId) {
          setUserId(storedId);
        } else {
          try {
            const res = await fetch(`${API_URL}/users/by_uid/${u.uid}`);
            const data = await res.json();
            if (data?.id) {
              localStorage.setItem('user_id', data.id);
              setUserId(data.id);
            }
          } catch (err) {
            console.error('Error obteniendo usuario:', err);
          }
        }
      } else {
        navigate('/login');
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  const cargarCitas = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      let fechaInicio, fechaFin;
      const fecha = new Date(fechaActual);

      if (vista === 'semana') {
        const diaSemana = fecha.getDay();
        fechaInicio = new Date(fecha);
        fechaInicio.setDate(fecha.getDate() - diaSemana);
        fechaInicio.setHours(0, 0, 0, 0);
        fechaFin = new Date(fechaInicio);
        fechaFin.setDate(fechaInicio.getDate() + 6);
        fechaFin.setHours(23, 59, 59, 999);
      } else {
        fechaInicio = new Date(fecha.getFullYear(), fecha.getMonth(), 1);
        fechaFin = new Date(fecha.getFullYear(), fecha.getMonth() + 1, 0, 23, 59, 59, 999);
      }

      const params = new URLSearchParams();
      params.append('fecha_inicio', fechaInicio.toISOString());
      params.append('fecha_fin', fechaFin.toISOString());
      params.append('medico_id', userId);  // Filtrar solo citas del médico actual
      if (filtroEstado) params.append('estado', filtroEstado);
      if (busqueda) params.append('buscar', busqueda);
      params.append('limit', '200');

      const [citasRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/emr/citas?${params}`, { headers: { 'X-User-Id': userId } }),
        fetch(`${API_URL}/emr/citas/estadisticas?medico_id=${userId}`, { headers: { 'X-User-Id': userId } })
      ]);

      if (citasRes.ok) setCitas((await citasRes.json()).citas || []);
      if (statsRes.ok) setEstadisticas(await statsRes.json());
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  }, [userId, fechaActual, vista, filtroEstado, busqueda]);

  useEffect(() => {
    if (userId) cargarCitas();
  }, [userId, cargarCitas]);

  // Cargar morning briefing al montar
  useEffect(() => {
    if (!userId) return;
    setLoadingBriefing(true);
    setShowBriefingAlerts(false);
    fetch(`${API_URL}/emr/ia/agenda-briefing`, { headers: { 'X-User-Id': userId } })
      .then(res => res.ok ? res.json() : null)
      .then(data => { if (data) setMorningBriefing(data); })
      .catch(e => console.error('Morning briefing error:', e))
      .finally(() => setLoadingBriefing(false));
  }, [userId]);

  const navegarAnterior = () => {
    const nueva = new Date(fechaActual);
    if (vista === 'semana') {
      nueva.setDate(nueva.getDate() - 7);
    } else {
      nueva.setMonth(nueva.getMonth() - 1);
    }
    setFechaActual(nueva);
  };

  const navegarSiguiente = () => {
    const nueva = new Date(fechaActual);
    if (vista === 'semana') {
      nueva.setDate(nueva.getDate() + 7);
    } else {
      nueva.setMonth(nueva.getMonth() + 1);
    }
    setFechaActual(nueva);
  };

  const getDiasSemana = () => {
    const dias = [];
    const fecha = new Date(fechaActual);
    const diaSemana = fecha.getDay();
    const inicioSemana = new Date(fecha);
    inicioSemana.setDate(fecha.getDate() - diaSemana);
    for (let i = 0; i < 7; i++) {
      const dia = new Date(inicioSemana);
      dia.setDate(inicioSemana.getDate() + i);
      dias.push(dia);
    }
    return dias;
  };

  const getDiasMes = () => {
    const fecha = new Date(fechaActual);
    const año = fecha.getFullYear();
    const mes = fecha.getMonth();
    const primerDia = new Date(año, mes, 1);
    const ultimoDia = new Date(año, mes + 1, 0);
    const dias = [];

    const primerDiaSemana = primerDia.getDay();
    for (let i = primerDiaSemana - 1; i >= 0; i--) {
      dias.push({ fecha: new Date(año, mes, -i), esOtroMes: true });
    }
    for (let i = 1; i <= ultimoDia.getDate(); i++) {
      dias.push({ fecha: new Date(año, mes, i), esOtroMes: false });
    }
    const diasRestantes = 42 - dias.length;
    for (let i = 1; i <= diasRestantes; i++) {
      dias.push({ fecha: new Date(año, mes + 1, i), esOtroMes: true });
    }
    return dias;
  };

  const citasDelDia = (fecha) => {
    return citas.filter(cita => new Date(cita.fecha_cita).toDateString() === fecha.toDateString())
      .sort((a, b) => new Date(a.fecha_cita) - new Date(b.fecha_cita));
  };

  const actualizarEstadoCita = async (citaId, nuevoEstado) => {
    try {
      const res = await fetch(`${API_URL}/emr/citas/${citaId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'X-User-Id': userId },
        body: JSON.stringify({ estado: nuevoEstado })
      });
      if (res.ok) {
        cargarCitas();
        setCitaSeleccionada(null);
      }
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const formatearHora = (fecha) => new Date(fecha).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });

  const diasSemana = getDiasSemana();
  const diasMes = getDiasMes();
  const hoy = new Date().toDateString();

  const getTitulo = () => {
    if (vista === 'semana') {
      return `${diasSemana[0].toLocaleDateString('es-MX', { month: 'long' })} ${diasSemana[0].getDate()} - ${diasSemana[6].getDate()}, ${fechaActual.getFullYear()}`;
    }
    return `${MESES[fechaActual.getMonth()]} ${fechaActual.getFullYear()}`;
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f1f5f9' }}>
      {/* Header */}
      <Box
        sx={{
          py: 2,
          px: { xs: 2, md: 4 }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', maxWidth: 1400, mx: 'auto' }}>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 22, color: '#1e293b' }}>Agenda de Citas</Typography>
            <Typography sx={{ fontSize: 13, color: '#64748b' }}>Citas agendadas desde chatbots</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={`${estadisticas?.total_hoy || 0} hoy`}
              size="small"
              sx={{ bgcolor: '#e2e8f0', color: '#475569', fontWeight: 600 }}
            />
            <IconButton onClick={cargarCitas} sx={{ color: '#64748b' }}>
              <RefreshIcon />
            </IconButton>
          </Box>
        </Box>
      </Box>

      {/* Contenido principal */}
      <Box sx={{ maxWidth: 1400, mx: 'auto', p: { xs: 2, md: 3 } }}>
        {/* Estadísticas */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          {[
            { label: 'Hoy', value: estadisticas?.total_hoy || 0, color: '#0ea5e9' },
            { label: 'Semana', value: estadisticas?.total_semana || 0, color: '#8b5cf6' },
            { label: 'Confirmadas', value: estadisticas?.confirmadas || 0, color: '#16a34a' },
            { label: 'Pendientes', value: estadisticas?.pendientes || 0, color: '#d97706' },
            { label: 'Canceladas', value: estadisticas?.canceladas || 0, color: '#dc2626' }
          ].map((stat) => (
            <Paper
              key={stat.label}
              sx={{ px: 2.5, py: 1.5, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1.5, border: '1px solid #e2e8f0', minWidth: 120 }}
            >
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: stat.color }} />
              <Box>
                <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#1e3a5f', lineHeight: 1 }}>{stat.value}</Typography>
                <Typography sx={{ fontSize: 11, color: '#64748b' }}>{stat.label}</Typography>
              </Box>
            </Paper>
          ))}
        </Box>

        {/* NARA Morning Briefing */}
        {(loadingBriefing || morningBriefing) && (
          <NaraTerminalCard
            label="nara@agenda ~ morning-briefing"
            loading={loadingBriefing}
            loadingText="Preparando tu briefing del día"
            width={{ xs: '100%', sm: 580 }}
          >
            {morningBriefing && (
              <Box>
                <NaraSimpleTypewriter
                  text={morningBriefing.resumen}
                  onComplete={() => setShowBriefingAlerts(true)}
                />
                {showBriefingAlerts && morningBriefing.alertas && morningBriefing.alertas.length > 0 && (
                  <Box sx={{ mt: 1, display: 'flex', gap: 0.75, flexWrap: 'wrap',
                    '@keyframes fadeIn': { from: { opacity: 0, transform: 'translateY(4px)' }, to: { opacity: 1, transform: 'translateY(0)' } },
                    animation: 'fadeIn 0.3s ease-out'
                  }}>
                    {morningBriefing.alertas.map((alerta, idx) => (
                      <Chip key={idx} label={alerta} size="small"
                        icon={<WarningIcon sx={{ fontSize: '12px !important' }} />}
                        sx={{
                          ...monoStyle, fontSize: 9.5, fontWeight: 600, height: 20,
                          bgcolor: 'rgba(245, 158, 11, 0.15)',
                          color: '#fbbf24',
                          border: '1px solid rgba(245, 158, 11, 0.3)',
                          '& .MuiChip-icon': { color: '#f59e0b' }
                        }}
                      />
                    ))}
                  </Box>
                )}
                {showBriefingAlerts && morningBriefing.pacientes_destacados && morningBriefing.pacientes_destacados.length > 0 && (
                  <Box sx={{
                    mt: 1, pt: 1, borderTop: '1px solid rgba(255,255,255,0.06)',
                    '@keyframes fadeIn': { from: { opacity: 0 }, to: { opacity: 1 } },
                    animation: 'fadeIn 0.3s ease-out'
                  }}>
                    {morningBriefing.pacientes_destacados.map((p, idx) => (
                      <Typography key={idx} sx={{ ...monoStyle, fontSize: 10, color: '#64748b', mb: 0.3 }}>
                        <Box component="span" sx={{ color: '#10b981' }}>paciente</Box>
                        <Box component="span" sx={{ color: '#475569' }}> :: </Box>
                        {p}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Box>
            )}
          </NaraTerminalCard>
        )}

        {/* Controles */}
        <Paper sx={{ p: 2, mb: 3, borderRadius: 2, border: '1px solid #e2e8f0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton size="small" onClick={navegarAnterior}>
                <ChevronLeftIcon />
              </IconButton>
              <Button
                variant="outlined"
                size="small"
                startIcon={<TodayIcon />}
                onClick={() => setFechaActual(new Date())}
                sx={{ borderRadius: 2, fontWeight: 600 }}
              >
                Hoy
              </Button>
              <IconButton size="small" onClick={navegarSiguiente}>
                <ChevronRightIcon />
              </IconButton>
              <Typography sx={{ fontWeight: 600, color: '#1e3a5f', ml: 1 }}>{getTitulo()}</Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <ToggleButtonGroup
                value={vista}
                exclusive
                onChange={(e, v) => v && setVista(v)}
                size="small"
              >
                <ToggleButton value="semana" sx={{ px: 2 }}>
                  <WeekViewIcon sx={{ fontSize: 18, mr: 0.5 }} /> Semana
                </ToggleButton>
                <ToggleButton value="mes" sx={{ px: 2 }}>
                  <MonthViewIcon sx={{ fontSize: 18, mr: 0.5 }} /> Mes
                </ToggleButton>
              </ToggleButtonGroup>

              <TextField
                size="small"
                placeholder="Buscar paciente..."
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                sx={{ width: 180, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment>
                }}
              />
              <FormControl size="small" sx={{ minWidth: 130 }}>
                <InputLabel>Estado</InputLabel>
                <Select value={filtroEstado} label="Estado" onChange={(e) => setFiltroEstado(e.target.value)} sx={{ borderRadius: 2 }}>
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="pendiente">Pendientes</MenuItem>
                  <MenuItem value="confirmado">Confirmadas</MenuItem>
                  <MenuItem value="cancelado">Canceladas</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>
        </Paper>

        {/* Calendario */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : vista === 'semana' ? (
          /* Vista Semanal */
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1.5 }}>
            {diasSemana.map((dia, idx) => {
              const citasDia = citasDelDia(dia);
              const esHoy = dia.toDateString() === hoy;

              return (
                <Paper
                  key={idx}
                  sx={{
                    borderRadius: 2,
                    overflow: 'hidden',
                    border: esHoy ? '2px solid #0ea5e9' : '1px solid #e2e8f0',
                    minHeight: 280
                  }}
                >
                  <Box
                    sx={{
                      p: 1.5,
                      background: esHoy ? 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)' : '#f8fafc',
                      borderBottom: '1px solid #e2e8f0',
                      textAlign: 'center'
                    }}
                  >
                    <Typography sx={{ fontSize: 11, fontWeight: 600, color: esHoy ? '#fff' : '#64748b' }}>
                      {DIAS_SEMANA[idx]}
                    </Typography>
                    <Typography sx={{ fontSize: 18, fontWeight: 700, color: esHoy ? '#fff' : '#1e3a5f' }}>
                      {dia.getDate()}
                    </Typography>
                  </Box>
                  <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 220, overflowY: 'auto' }}>
                    {citasDia.length === 0 ? (
                      <Typography sx={{ fontSize: 11, color: '#94a3b8', textAlign: 'center', py: 2 }}>Sin citas</Typography>
                    ) : (
                      citasDia.map((cita) => {
                        const estado = estadoColors[cita.estado] || estadoColors.null;
                        return (
                          <Box
                            key={cita.id}
                            onClick={() => seleccionarCita(cita)}
                            sx={{
                              p: 1,
                              borderRadius: 1.5,
                              bgcolor: estado.bg,
                              border: `1px solid ${estado.color}20`,
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              '&:hover': { transform: 'scale(1.02)', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                              <AccessTimeIcon sx={{ fontSize: 12, color: estado.color }} />
                              <Typography sx={{ fontSize: 11, fontWeight: 700, color: estado.color }}>
                                {formatearHora(cita.fecha_cita)}
                              </Typography>
                            </Box>
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#1e3a5f', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {cita.paciente_nombre}
                            </Typography>
                            <Typography sx={{ fontSize: 10, color: '#64748b' }}>{cita.numero_whatsapp}</Typography>
                          </Box>
                        );
                      })
                    )}
                  </Box>
                </Paper>
              );
            })}
          </Box>
        ) : (
          /* Vista Mensual */
          <Paper sx={{ p: 2, borderRadius: 2, border: '1px solid #e2e8f0' }}>
            {/* Encabezado días */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1, mb: 1 }}>
              {DIAS_SEMANA.map(dia => (
                <Typography key={dia} sx={{ textAlign: 'center', fontSize: 12, fontWeight: 600, color: '#64748b', py: 1 }}>
                  {dia}
                </Typography>
              ))}
            </Box>
            {/* Días del mes */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1 }}>
              {diasMes.map(({ fecha, esOtroMes }, idx) => {
                const citasDia = citasDelDia(fecha);
                const esHoy = fecha.toDateString() === hoy;
                const tieneCitas = citasDia.length > 0;

                return (
                  <Box
                    key={idx}
                    onClick={() => tieneCitas && seleccionarCita(citasDia[0])}
                    sx={{
                      p: 1,
                      minHeight: 70,
                      borderRadius: 1.5,
                      bgcolor: esHoy ? '#eff6ff' : esOtroMes ? '#fafafa' : '#fff',
                      border: esHoy ? '2px solid #0ea5e9' : '1px solid #e2e8f0',
                      cursor: tieneCitas ? 'pointer' : 'default',
                      transition: 'all 0.15s',
                      '&:hover': tieneCitas ? { bgcolor: '#f1f5f9', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' } : {}
                    }}
                  >
                    <Typography sx={{
                      fontSize: 13,
                      fontWeight: esHoy ? 700 : 500,
                      color: esOtroMes ? '#94a3b8' : esHoy ? '#0ea5e9' : '#1e3a5f',
                      mb: 0.5
                    }}>
                      {fecha.getDate()}
                    </Typography>
                    {tieneCitas && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {citasDia.slice(0, 3).map(cita => {
                          const colores = estadoColors[cita.estado] || estadoColors.null;
                          return (
                            <Box key={cita.id} sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: colores.color }} />
                          );
                        })}
                        {citasDia.length > 3 && (
                          <Typography sx={{ fontSize: 10, color: '#64748b' }}>+{citasDia.length - 3}</Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Paper>
        )}

        {/* Panel de detalle de cita seleccionada */}
        {citaSeleccionada && (
          <Paper sx={{ mt: 2, p: 2, borderRadius: 2, border: '1px solid #e2e8f0' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
              <Box>
                <Typography sx={{ fontWeight: 700, color: '#1e3a5f', fontSize: 16, mb: 0.5 }}>
                  {citaSeleccionada.paciente_nombre}
                </Typography>
                <Box sx={{ display: 'flex', gap: 3, mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <AccessTimeIcon sx={{ fontSize: 14, color: '#6366f1' }} />
                    <Typography sx={{ fontSize: 13 }}>
                      {new Date(citaSeleccionada.fecha_cita).toLocaleDateString('es-MX')} - {formatearHora(citaSeleccionada.fecha_cita)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <WhatsAppIcon sx={{ fontSize: 14, color: '#25d366' }} />
                    <Typography sx={{ fontSize: 13 }}>{citaSeleccionada.numero_whatsapp}</Typography>
                  </Box>
                </Box>
                <Chip
                  label={citaSeleccionada.estado || 'Pendiente'}
                  size="small"
                  sx={{
                    bgcolor: (estadoColors[citaSeleccionada.estado] || estadoColors.null).bg,
                    color: (estadoColors[citaSeleccionada.estado] || estadoColors.null).color,
                    fontWeight: 600
                  }}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {(citaSeleccionada.estado !== 'confirmado' && citaSeleccionada.estado !== 'cancelado') && (
                  <>
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      startIcon={<CheckCircleIcon />}
                      onClick={() => actualizarEstadoCita(citaSeleccionada.id, 'confirmado')}
                      sx={{ borderRadius: 2 }}
                    >
                      Confirmar
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      startIcon={<CancelIcon />}
                      onClick={() => actualizarEstadoCita(citaSeleccionada.id, 'cancelado')}
                      sx={{ borderRadius: 2 }}
                    >
                      Cancelar
                    </Button>
                  </>
                )}
                <Button size="small" variant="outlined" onClick={() => seleccionarCita(null)} sx={{ borderRadius: 2 }}>
                  Cerrar
                </Button>
              </Box>
            </Box>
          </Paper>
        )}
      </Box>
    </Box>
  );
};

export default AgendaCitasPage;
